import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";

import {
  T,
  formatClock,
  OPERATIONAL_STATUS,
  PHOTO_STAGE_LABEL,
  PHOTO_STAGES,
  PICKUP_IN_PROGRESS_STATUSES,
  TERMINAL_BOOKING_STATUSES,
} from "../constants";
import { useUi, cx } from "../ui";
import {
  formatMoney,
  paymentTag,
  vehicleLabel,
  withFullBooking,
} from "../helpers";
import sessionHistory from "../sessionHistory";
import {
  conciergeBookingDetail,
  conciergeBookingView,
  vehiclePhotoStages,
} from "../dashboard/queries";
import ChargesPanel from "./ChargesPanel";
import StagePhotosModal from "./StagePhotosModal";
import usePickupCharge from "../dashboard/usePickupCharge";
import { useInstallation } from "../../../components/InstallationContext";
import LoadingIndicator from "../../../components/LoadingIndicator";
import authManager, { PERMISSIONS } from "../../../lib/authManager";
import ModalCard from "./ModalCard";
import PickupProgress from "./PickupProgress";
import Plate from "./Plate";
import Tag from "./Tag";
import VipBadge from "./VipBadge";

const NOT_PROVIDED = "Not provided";

const BOOKING_STATUS_LABEL = {
  PENDING_ENTRY: "Expected",
  CURRENT: "Current",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

const useStyles = makeStyles({
  head: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  service: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: T.fg,
  },
  ids: {
    fontSize: 12,
    color: T.mutedFg,
    marginBottom: 10,
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
  },
  section: { fontSize: 14, fontWeight: 600, color: T.fg, margin: "14px 0 4px" },
  row: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: 12,
    padding: "6px 0",
    borderBottom: `1px solid ${T.border}`,
    fontSize: 13,
  },
  period: {
    display: "grid",
    gridTemplateColumns: "140px 1fr 1fr",
    gap: 12,
    padding: "6px 0",
    borderBottom: `1px solid ${T.border}`,
    fontSize: 13,
  },
  label: { color: T.mutedFg },
  value: { color: T.fg, minWidth: 0, overflowWrap: "anywhere" },
  historyBox: {
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
    background: T.elevated,
  },
  historyTitle: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: T.subtleFg,
    fontWeight: 600,
  },
  historyRow: {
    display: "grid",
    gridTemplateColumns: "130px 1fr",
    gap: 10,
    color: T.fg,
  },
  steps: {
    display: "flex",
    gap: "4px 12px",
    flexWrap: "wrap",
    fontSize: 12,
    color: T.mutedFg,
    fontVariantNumeric: "tabular-nums",
  },
  empty: { fontSize: 13, color: T.mutedFg, padding: "4px 0 8px" },
  photoStages: { display: "flex", gap: "4px 10px", flexWrap: "wrap" },
  photoLink: {
    border: "none",
    background: "transparent",
    padding: 0,
    font: "inherit",
    color: T.primary,
    cursor: "pointer",
    textDecoration: "underline",
  },
  more: {
    width: "100%",
    marginTop: 14,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    fontWeight: 600,
    color: T.primary,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    justifyContent: "space-between",
    "&:hover": { background: T.surface },
  },
  moreHint: { fontWeight: 400, color: T.mutedFg },
  actions: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 },
  small: { padding: "7px 12px", fontSize: 12 },
  footerActions: { display: "inline-flex", gap: 8, alignItems: "center" },
  /* Holds the body's height so the modal does not jump once it lands. */
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 220,
  },
});

/*
 * Nothing is worth reading until the booking lands. Dashes everywhere would
 * read as "this booking has none" when the truth is "not yet".
 */
function Loading() {
  const s = useStyles();
  return (
    <div className={s.loading} aria-busy="true">
      <LoadingIndicator />
    </div>
  );
}

function DetailRow({ label, children }) {
  const s = useStyles();
  return (
    <div className={s.row}>
      <span className={s.label}>{label}</span>
      <span className={s.value}>{children ?? "—"}</span>
    </div>
  );
}

/* Time of day and the day itself, in two columns, as the desk reads a stay. */
function PeriodRow({ label, iso }) {
  const s = useStyles();
  const at = iso ? new Date(iso) : null;

  return (
    <div className={s.period}>
      <span className={s.label}>{label}</span>
      <span className={s.value}>{at ? `${formatClock(iso)} local` : "—"}</span>
      <span className={s.value}>
        {at ? at.toLocaleDateString([], { dateStyle: "medium" }) : "—"}
      </span>
    </div>
  );
}

const dayAndTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const nightsOf = (row) =>
  row.checkIn && row.checkOut
    ? Math.max(
        1,
        Math.round(
          (new Date(row.checkOut).getTime() - new Date(row.checkIn).getTime()) /
            86400000,
        ),
      )
    : 1;

/*
 * One booking view for both audiences. The desk and the runner hand it the same
 * row - mapSessionToArrival flattens the full booking and the narrow runner
 * projection into one shape - and it renders the same sections either way.
 * What separates them is which actions the caller hands in, and what the server
 * chose to put in the row: the runner's projection carries no guest contact
 * details and no money, so those rows read Not provided rather than being
 * hidden here.
 */
export default function BookingQuickView({
  row,
  facilityId,
  action,
  secondaryAction,
  actionBusy,
  onAction,
  onEdit,
  footerLeft,
  onClose,
}) {
  const s = useStyles();
  const ui = useUi();
  const { installation } = useInstallation() ?? {};
  const [more, setMore] = useState(false);
  /* Which stage's set is open over this modal, if any. */
  const [stagePhotos, setStagePhotos] = useState(null);

  const pickup =
    row.status === "REQUESTED" ||
    PICKUP_IN_PROGRESS_STATUSES.includes(row.status);
  /* The queue labels its own rows; a desk row arrives raw. A row with no
     session at all is a booking, and the booking's own status is the answer. */
  const statusLabel =
    row.statusLabel ??
    OPERATIONAL_STATUS[row.status] ??
    row.status ??
    BOOKING_STATUS_LABEL[row.bookingStatus] ??
    null;

  /* The desk settles the stay here. Pickup fees belong to the request that
   * incurs them, and are taken with it. */
  const charge = usePickupCharge(row.bookingId, { scope: "STAY" });
  /* Putting a balance on a card is desk work. A runner sees what is owed and
     who has to clear it. */
  const canCharge = authManager.hasPermission(
    PERMISSIONS.BOOKINGS_MANAGE,
    installation?.id,
  );

  /*
   * The runner queue's list query carries the narrow projection, so a concierge
   * working that queue arrives here without the guest's details. They are
   * entitled to them, so fetch the booking for this one row - the server
   * refuses anyone who is not.
   */
  const { data: bookingData, loading: bookingLoading } = useQuery(
    conciergeBookingView,
    {
      variables: { bookingId: row.bookingId },
      skip:
        !row.narrow ||
        !row.bookingId ||
        !authManager.hasPermission(PERMISSIONS.BOOKINGS_READ, installation?.id),
    },
  );

  const { data, loading: detailLoading } = useQuery(conciergeBookingDetail, {
    variables: { bookingId: row.bookingId },
    skip: !more || !row.bookingId,
    fetchPolicy: "network-only"
  });

  /* Counts only, for the history line. A stage the desk opens fetches its own
     photos with their presigned links. */
  const { data: photoData } = useQuery(vehiclePhotoStages, {
    variables: { bookingId: row.bookingId, facilityId },
    skip: !row.bookingId || !facilityId,
    fetchPolicy: "cache-and-network",
  });

  const photoStages = [
    { stage: PHOTO_STAGES.INTAKE, photos: photoData?.intake ?? [] },
    { stage: PHOTO_STAGES.PARKING, photos: photoData?.parking ?? [] },
    { stage: PHOTO_STAGES.HANDOVER, photos: photoData?.handover ?? [] },
  ];

  const view = withFullBooking(row, bookingData?.booking);

  /* A booking that has stopped moving has nothing left to amend. */
  const editable =
    Boolean(view.bookingId) &&
    !TERMINAL_BOOKING_STATUSES.includes(view.bookingStatus);

  const detail = data?.booking ?? null;
  const history = sessionHistory(view);
  const payment = paymentTag(view);
  const nights = nightsOf(view);
  const location =
    [view.lotName, view.bay && `Bay ${view.bay}`].filter(Boolean).join(" · ") ||
    (view.status === "CLAIMED" || view.status === "PARKING"
      ? "To be confirmed when parked"
      : "Forecourt");

  return (
    <ModalCard
      title={
        <div className={s.head}>
          <span className={s.service}>
            {view.lotType === "OFFSITE" ? "OFFSITE" : "VALET"}
          </span>
          {statusLabel && (
            <Tag tone="info" size={10} upper>
              {statusLabel}
            </Tag>
          )}
          {payment.label !== "Paid" && (
            <Tag tone={payment.tone} size={10} upper>
              {payment.label}
            </Tag>
          )}
          <Plate size={14}>{view.plate}</Plate>
          <strong>{view.guestName || "Unmatched plate"}</strong>
          {view.vip && <VipBadge />}
        </div>
      }
      onClose={onClose}
      width={720}
      z={1300}
      footerLeft={
        footerLeft ?? (
          <button
            type="button"
            className={cx(ui.mutedBtn, s.small)}
            onClick={onClose}
          >
            Close
          </button>
        )
      }
      footerRight={
        (action || secondaryAction) && (
          <span className={s.footerActions}>
            {secondaryAction && (
              <button
                type="button"
                className={cx(ui.mutedBtn, s.small)}
                disabled={secondaryAction.disabled === true || actionBusy}
                title={secondaryAction.reason ?? undefined}
                onClick={() => onAction(row, secondaryAction)}
              >
                {secondaryAction.label}
              </button>
            )}
            {action && (
              <button
                type="button"
                className={cx(ui.primaryBtn, s.small)}
                disabled={action.disabled === true || actionBusy}
                title={action.reason ?? undefined}
                onClick={() => onAction(row, action)}
              >
                {action.label}
              </button>
            )}
          </span>
        )
      }
    >
      {bookingLoading ? (
        <Loading />
      ) : (
        <>
          <div className={s.ids}>
            <span>
              Booking Id: {view.bookingId ? `#${view.bookingId}` : NOT_PROVIDED}
            </span>
            <span>Detected: {dayAndTime(view.arrivedAt)}</span>
          </div>

          <DetailRow label="Guest card">
            {view.guestCard ? (
              <>
                {view.guestCard}{" "}
                <Tag tone="info" size={9} upper>
                  {view.lotType === "OFFSITE" ? "Offsite" : "Valet"}
                </Tag>
              </>
            ) : (
              NOT_PROVIDED
            )}
          </DetailRow>
          <DetailRow label="VIP booking">
            {view.vip ? "Yes" : "No"}
          </DetailRow>

          <div className={s.section}>Guest details</div>
          <DetailRow label="Name">
            {view.guestName || NOT_PROVIDED}
          </DetailRow>
          <DetailRow label="Email">
            {view.email || NOT_PROVIDED}
          </DetailRow>
          <DetailRow label="Mobile">
            {view.phone || NOT_PROVIDED}
          </DetailRow>

          <div className={s.section}>Booking period</div>
          <PeriodRow label="Booking start" iso={view.checkIn} />
          <PeriodRow label="Booking end" iso={view.checkOut} />
          <PeriodRow label="First entry" iso={view.arrivedAt} />
          <PeriodRow label="Last exit" iso={view.returnedAt} />

          <div className={s.section}>Vehicle</div>
          <DetailRow label="Vehicle">
            {vehicleLabel(row) ?? NOT_PROVIDED}
          </DetailRow>
          <DetailRow label="Location">{location}</DetailRow>
          <DetailRow label="Porter">
            {view.runnerName ??
              view.pickedByName ??
              view.parkedByName ??
              "Not yet assigned"}
          </DetailRow>
          {pickup && <PickupProgress status={view.status} />}

          <div className={s.section}>Parking fee</div>
          <DetailRow label="Booked">
            {view.amount === null || view.amount === undefined
              ? NOT_PROVIDED
              : `${nights} ${nights === 1 ? "day" : "days"} · ${formatMoney(
                  view.amount,
                  charge.currency || "AUD",
                )}`}
          </DetailRow>
          <DetailRow label="Payment">
            {payment.label}
            {charge.stayOutstanding > 0
              ? ` · ${formatMoney(charge.stayOutstanding, charge.currency)} outstanding`
              : ""}
          </DetailRow>

          {charge.hasCharges ? (
            <>
              <div className={s.section}>Charges</div>
              <ChargesPanel
                charge={charge}
                bookingId={view.bookingId}
                hasBooking={Boolean(view.bookingId)}
                canCharge={canCharge}
              />
            </>
          ) : null}

          <div className={s.section}>Operational history</div>
          <div className={s.historyBox} aria-label="Operational history">
            <span className={s.historyTitle}>Handling</span>
            <div className={s.historyRow}>
              <span className={s.label}>Received</span>
              <span>
                {view.claimedAt
                  ? `${dayAndTime(view.claimedAt)}`
                  : "Not yet received"}
              </span>
            </div>
            <div className={s.historyRow}>
              <span className={s.label}>Parked</span>
              <span>
                {view.parkedAt
                  ? `${view.parkedByName ? `${view.parkedByName} · ` : ""}${dayAndTime(
                      view.parkedAt,
                    )} · ${view.parkedLotName ?? "lot not recorded"}${
                      view.bay ? ` · Bay ${view.bay}` : ""
                    }`
                  : "Not yet parked"}
              </span>
            </div>
            <div className={s.historyRow}>
              <span className={s.label}>Photos</span>
              <span className={s.photoStages}>
                {photoStages.map(({ stage, photos }) => (
                  <span key={stage}>
                    {PHOTO_STAGE_LABEL[stage]}:{" "}
                    {photos.length === 0 ? (
                      "Not recorded"
                    ) : (
                      <button
                        type="button"
                        className={s.photoLink}
                        onClick={() => setStagePhotos(stage)}
                      >
                        {photos.length} taken
                      </button>
                    )}
                  </span>
                ))}
              </span>
            </div>
            <div className={s.historyRow}>
              <span className={s.label}>Porter</span>
              <span>
                {view.pickedByName ?? view.parkedByName ?? "No porter yet"}
              </span>
            </div>

            <span className={s.historyTitle} style={{ marginTop: 6 }}>
              Pickup
            </span>
            {history.length === 0 ? (
              <div className={s.empty}>Nothing recorded yet.</div>
            ) : (
              <div className={s.steps}>
                {history.map((entry) => (
                  <span key={entry.key}>
                    {entry.label} {formatClock(entry.at)}
                  </span>
                ))}
              </div>
            )}
          </div>

        </>
      )}

      {view.bookingId && !bookingLoading && (
        <button
          type="button"
          className={s.more}
          aria-expanded={more}
          onClick={() => setMore((current) => !current)}
        >
          <span>{more ? "Less ▴" : "More ▾"}</span>
          <span className={s.moreHint}>
            Parking sessions, emails, fee breakdown
          </span>
        </button>
      )}

      {more && detailLoading && <Loading />}

      {more && !detailLoading && (
        <div aria-label="More booking details">
          <div className={s.section}>
            Parking sessions ({detail?.valetSessions?.length ?? 0})
          </div>
          {(detail?.valetSessions ?? []).length === 0 ? (
            <div className={s.empty}>No valet sessions recorded.</div>
          ) : (
            detail.valetSessions.map((item) => (
              <DetailRow key={item.id} label={dayAndTime(item.arrivedAt)}>
                {OPERATIONAL_STATUS[item.status] ?? item.status}
                {item.bay || item.parkedLocation
                  ? ` · Bay ${item.bay ?? item.parkedLocation}`
                  : ""}
              </DetailRow>
            ))
          )}

          <div className={s.section}>Email history</div>
          {(detail?.emails ?? []).length === 0 ? (
            <div className={s.empty}>No emails recorded.</div>
          ) : (
            detail.emails.map((email) => (
              <DetailRow key={email.id} label={dayAndTime(email.createdAt)}>
                {email.templateClass}{" "}
                <Tag
                  tone={email.finalisedAt ? "ok" : "muted"}
                  size={9}
                  upper
                >
                  {email.finalisedAt ? "Sent" : "Pending"}
                </Tag>
              </DetailRow>
            ))
          )}

          <div className={s.section}>Parking fee breakdown</div>
          {charge.breakdown && charge.hasCharges ? (
            <>
              <DetailRow label="Stay">{charge.breakdown.bookingAud}</DetailRow>
              <DetailRow label="Overage">
                {charge.breakdown.overageAud}
              </DetailRow>
              <DetailRow label="Pickup fees">
                {charge.breakdown.valetRequestFeeAud} ·{" "}
                {charge.breakdown.valetRequestFeeDueAud} due
              </DetailRow>
              <DetailRow label="Paid">{charge.breakdown.paidAud}</DetailRow>
            </>
          ) : (
            <div className={s.empty}>No breakdown available.</div>
          )}

          {onEdit && editable && (
            <div className={s.actions}>
              <button
                type="button"
                className={cx(ui.primaryBtn, s.small)}
                onClick={() => onEdit(view)}
              >
                Edit booking
              </button>
            </div>
          )}
        </div>
      )}

      {stagePhotos && (
        <StagePhotosModal
          row={view}
          facilityId={facilityId}
          stage={stagePhotos}
          onClose={() => setStagePhotos(null)}
        />
      )}
    </ModalCard>
  );
}
